// @flow
import * as React from 'react'
import is from 'mz-utils/is'
import { FormattedWithChildren } from 'mz-intl'
import Autosizer from './autosize'
import {
  HTMLInputElement,
  HTMLSelectElement,
  StaticValueContainer,
  HTMLTextareaElement,
} from './elements'

// Types
import type {
  Styles,
  Input as InputProps,
  Props as BaseProps,
} from '../BaseField'

export type InputComponentProps = BaseProps & {
  autoCapitalize?: string,
  autoComplete?: string,
}
export type Props = {
  innerRef?: (?any) => any,
  tabIndex?: any,
  input: InputProps,
  style: Styles,
  props: InputComponentProps,
  children?: React.Node,
}

export type TextAreaProps = Props & {
  inputRef?: (?any) => any,
  stringPlaceholder?: string,
}

// Utils
const getValueByInput = (input: ?InputProps) => {
  if (!input || !input.onChange) return undefined
  return is.undef(input.value) ? '' : input.value
}

/**
 * General editable input component based on HTML's `<input>` tag
 * @return {React.Element}
 */
export const EditableInput = ({ input, style, props, innerRef }: Props) => (
  <FormattedWithChildren message={props.placeholder}>
    {stringPlaceholder => (
      <HTMLInputElement
        {...input}
        value={getValueByInput(input)}
        {...style}
        ref={innerRef}
        id={props.id}
        name={props.name || input.name}
        type={props.type || 'text'}
        placeholder={stringPlaceholder}
        disabled={props.disabled}
        autoCorrect="off"
        autoCapitalize={props.autoCapitalize || 'off'}
        autoComplete={props.autoComplete || 'off'}
        spellCheck="false"
      />
    )}
  </FormattedWithChildren>
)

// FIXME: Component added because we need a stable function provided to the ref,
// otherwise react re-render the field with new dom element for some reason
export class TextAreaWithStableRef extends React.Component<TextAreaProps> {
  setInnerRef = (e: Object) => {
    const { innerRef, inputRef } = this.props

    innerRef && innerRef(e)
    inputRef && inputRef(e)
  }

  render() {
    const { input, style, props, stringPlaceholder } = this.props
    return (
      <HTMLTextareaElement
        {...input}
        value={getValueByInput(input)}
        {...style}
        ref={this.setInnerRef}
        id={props.id}
        name={props.name || input.name}
        type={props.type || 'text'}
        placeholder={stringPlaceholder}
        disabled={props.disabled}
        autoCorrect="off"
        autoCapitalize="off"
        autoComplete="off"
        spellCheck="false"
        rows="1"
      />
    )
  }
}

/**
 * A field for showing textarea with empty styles
 */
export const EditableTextarea = ({
  input = {},
  style,
  props,
  innerRef,
}: Props) => (
  <FormattedWithChildren message={props.placeholder}>
    {stringPlaceholder => (
      <Autosizer
        render={inputRef => (
          <TextAreaWithStableRef
            input={input}
            style={style}
            props={props}
            stringPlaceholder={stringPlaceholder}
            innerRef={innerRef}
            inputRef={inputRef}
          />
        )}
      />
    )}
  </FormattedWithChildren>
)

/**
 * Static (non-editable) input component based on HTML's `<div>` tag
 * @return {React.Element}
 */
export const StaticInput = ({
  input,
  style,
  props,
  innerRef,
  tabIndex = 0,
}: Props) => (
  <StaticValueContainer
    {...input}
    {...style}
    id={props.id}
    value={undefined}
    ref={innerRef}
    tabIndex={tabIndex}
  >
    {style.$nonEmpty ? input.value : props.placeholder}
  </StaticValueContainer>
)

/**
 * Static (non-editable) input component based on HTML's `<div>` tag
 * @return {React.Element}
 */
export const SelectInput = ({
  input,
  style,
  props,
  innerRef,
  children,
}: Props) => (
  <HTMLSelectElement
    {...input}
    {...style}
    id={props.id}
    name={props.name}
    ref={innerRef}
  >
    <FormattedWithChildren message={props.placeholder}>
      {stringPlaceholder => (
        <option value="" disabled>
          {stringPlaceholder}
        </option>
      )}
    </FormattedWithChildren>
    {children}
  </HTMLSelectElement>
)

/**
 * Simple wrapper which decides what input implementation to use
 * based on given "static" props
 * @return {React.Element}
 */
const Input = (props: Props) => {
  if (props.props && props.props.type === 'select') {
    return <SelectInput {...props} />
  }
  if (props.props.type === 'static') {
    return <StaticInput {...props} />
  }
  if (props.props.type === 'textarea') {
    return <EditableTextarea {...props} />
  }
  return <EditableInput {...props} />
}

export default Input
